import React from "react";
import { Easing, interpolate, useCurrentFrame } from "remotion";
import { useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { Footnote, Headline, Kicker, Split, Sub } from "../ui/Split";
import { Sfx, Ticks } from "../ui/Sfx";
import { Card } from "../ui/Surfaces";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Scene 1 — the pain, in the first three seconds (LinkedIn autoplays
 * muted, so it has to read, not sound). CAC up more than 60 % in five
 * years (Paddle, ex-ProfitWell, as cited in the blog post "L'acquisition
 * client est cassée"): the number counts up while the curve climbs.
 *
 * Frame 0 shows the kicker and the empty chart frame; the curve draws
 * from `DRAW_FROM`. The poster / LinkedIn thumbnail is frame 106 (end of
 * the hook, everything landed): upload it as the cover, don't rely on the
 * first frame.
 */

const CHART = { w: 880, h: 580 };
const PLOT = { x: 56, y: 130, w: 768, h: 330 };
/** CAC index, 5 years ago → today (100 → 160 = +60 %). */
const SERIES = [100, 107, 119, 128, 146, 160];
const Y_MIN = 80;
const Y_MAX = 172;

const DRAW_FROM = 4;
const DRAW_TO = 40;

const pointAt = (i: number) => ({
  x: PLOT.x + (i / (SERIES.length - 1)) * PLOT.w,
  y: PLOT.y + PLOT.h - ((SERIES[i] - Y_MIN) / (Y_MAX - Y_MIN)) * PLOT.h,
});

/** Point on the polyline at progress t (0..1 of the x range). */
const pointAlong = (t: number) => {
  const f = t * (SERIES.length - 1);
  const i = Math.min(SERIES.length - 2, Math.floor(f));
  const a = pointAt(i);
  const b = pointAt(i + 1);
  const k = f - i;
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
};

const Chart: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const t = interpolate(frame, [DRAW_FROM, DRAW_TO], [0, 1], {
    ...clamp,
    easing: Easing.bezier(0.45, 0, 0.2, 1),
  });
  const pts = SERIES.map((_, i) => pointAt(i));
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p.x},${p.y}`).join(" ");
  const area = `${line} L${PLOT.x + PLOT.w},${PLOT.y + PLOT.h} L${PLOT.x},${PLOT.y + PLOT.h} Z`;
  const head = pointAlong(t);
  const pulse =
    frame > DRAW_TO ? Math.sin((frame - DRAW_TO) / 4) * 0.5 + 0.5 : 0;

  return (
    <Card style={{ width: CHART.w, height: CHART.h }}>
      <div
        style={{
          position: "absolute",
          left: PLOT.x,
          top: 44,
          fontFamily: LOOK.body,
          fontWeight: LOOK.semi,
          fontSize: 30,
          color: LOOK.ink,
        }}
      >
        {copy.hook.chartTitle}
      </div>
      <svg
        width={CHART.w}
        height={CHART.h}
        style={{ position: "absolute", inset: 0 }}
      >
        <defs>
          <linearGradient id="hook-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={LOOK.red} stopOpacity={0.22} />
            <stop offset="100%" stopColor={LOOK.red} stopOpacity={0} />
          </linearGradient>
          <clipPath id="hook-reveal">
            <rect x={0} y={0} width={head.x} height={CHART.h} />
          </clipPath>
        </defs>
        {[0, 1, 2, 3].map((i) => (
          <line
            key={i}
            x1={PLOT.x}
            x2={PLOT.x + PLOT.w}
            y1={PLOT.y + (i / 3) * PLOT.h}
            y2={PLOT.y + (i / 3) * PLOT.h}
            stroke={LOOK.hairline}
            strokeWidth={2}
            strokeDasharray={i === 3 ? undefined : "6 10"}
          />
        ))}
        <g clipPath="url(#hook-reveal)">
          <path d={area} fill="url(#hook-area)" />
          <path
            d={line}
            fill="none"
            stroke={LOOK.red}
            strokeWidth={10}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </g>
        {/* Start point: where the cost was. */}
        <circle
          cx={pts[0].x}
          cy={pts[0].y}
          r={11}
          fill={LOOK.white}
          stroke={LOOK.red}
          strokeWidth={6}
        />
        {/* Moving head, pulsing once it arrives. */}
        {t > 0 ? (
          <>
            <circle
              cx={head.x}
              cy={head.y}
              r={22 + pulse * 12}
              fill={LOOK.red}
              opacity={0.18}
            />
            <circle
              cx={head.x}
              cy={head.y}
              r={14}
              fill={LOOK.red}
              stroke={LOOK.white}
              strokeWidth={5}
            />
          </>
        ) : null}
      </svg>
      {[copy.hook.from, copy.hook.to].map((label, i) => (
        <div
          key={label}
          style={{
            position: "absolute",
            top: PLOT.y + PLOT.h + 26,
            left: i ? undefined : PLOT.x,
            right: i ? CHART.w - PLOT.x - PLOT.w : undefined,
            fontFamily: LOOK.body,
            fontWeight: LOOK.medium,
            fontSize: 26,
            color: i ? LOOK.ink : LOOK.muted,
          }}
        >
          {label}
        </div>
      ))}
    </Card>
  );
};

export const Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const stage = useStage();

  const value = interpolate(frame, [DRAW_FROM, DRAW_TO], [0, copy.hook.value], {
    ...clamp,
    easing: Easing.bezier(0.45, 0, 0.2, 1),
  });
  const numberSize = stage.shape === "wide" ? 200 : 150;

  return (
    <>
      <Split
        background={LOOK.white}
        visualSize={CHART}
        textH={stage.shape === "tall" ? 470 : 430}
        text={
          <>
            <Kicker text={copy.hook.kicker} />
            <div
              style={{
                fontFamily: LOOK.display,
                fontWeight: LOOK.bold,
                fontSize: numberSize,
                lineHeight: 0.92,
                letterSpacing: -numberSize * 0.045,
                color: LOOK.red,
                fontVariantNumeric: "tabular-nums",
                opacity: interpolate(
                  frame,
                  [DRAW_FROM, DRAW_FROM + 3],
                  [0, 1],
                  clamp,
                ),
              }}
            >
              +{Math.round(value)}
              {copy.pct}
            </div>
            <div style={{ marginTop: 6 }}>
              <Headline text={copy.hook.line} start={14} />
            </div>
            <Sub text={copy.hook.sub} at={42} />
          </>
        }
        visual={<Chart />}
      >
        <Footnote text={copy.hook.source} at={50} />
      </Split>
      {/* The counter runs up, then the bad news lands. */}
      <Ticks from={DRAW_FROM} to={DRAW_TO} />
      <Sfx sound="negative" at={DRAW_TO} />
    </>
  );
};
